import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ObtenerMetodosPago,
  obtenerResumenCheckoutPago,
  confirmarCheckoutPago,
} from "../../../../services/store/Checkout";
import { useConfiguracionTiendaPublic } from "../../hooks/useConfiguracionTiendaPublic";
import "./CheckoutPago.css";
import "./CheckoutMetodoEnvio.css";

function CheckoutPagoStepper() {
  return (
    <div className="checkout-pago-topbar">
      <div className="checkout-pago-brand">
        <span className="checkout-pago-brand-dot" />
        Paso 3
      </div>
      <div className="checkout-pago-steps">
        <div className="checkout-pago-step is-done">
          <span>1</span>
          <p>Datos</p>
        </div>
        <div className="checkout-pago-step-line is-active" />
        <div className="checkout-pago-step is-done">
          <span>2</span>
          <p>Envío</p>
        </div>
        <div className="checkout-pago-step-line is-active" />
        <div className="checkout-pago-step is-active">
          <span>3</span>
          <p>Pago</p>
        </div>
      </div>
    </div>
  );
}


export default function CheckoutPago() {
  const navigate = useNavigate();
  const { grupo_reserva } = useParams();
  const { formatMoney } = useConfiguracionTiendaPublic();

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [resumenPago, setResumenPago] = useState(null);
  const [metodoSeleccionado, setMetodoSeleccionado] = useState(null);
  const [comprobanteFile, setComprobanteFile] = useState(null);

  const comprobanteInputRef = useRef(null);
  const submitLockRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    const cargar = async () => {
      try {
        setLoading(true);
        setError("");

        const [responseMetodos, responseResumen] = await Promise.all([
          ObtenerMetodosPago(grupo_reserva),
          obtenerResumenCheckoutPago(grupo_reserva),
        ]);

        if (!mounted) return;

        setData(responseMetodos);
        setResumenPago(responseResumen?.data || null);

        if (
          Array.isArray(responseMetodos?.metodos_pago) &&
          responseMetodos.metodos_pago.length
        ) {
          setMetodoSeleccionado(responseMetodos.metodos_pago[0]);
        }
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(
          err?.response?.data?.message ||
            "No se pudieron obtener los métodos de pago."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    cargar();

    return () => {
      mounted = false;
    };
  }, [grupo_reserva]);

  const metodos = useMemo(() => {
    return Array.isArray(data?.metodos_pago) ? data.metodos_pago : [];
  }, [data]);

  const metodoRequiereComprobante = useMemo(() => {
    if (!metodoSeleccionado) return false;
    if (metodoSeleccionado.requiere_comprobante === true) return true;
    return Number(metodoSeleccionado.requiere_comprobante) === 1;
  }, [metodoSeleccionado]);

  useEffect(() => {
    if (!metodoRequiereComprobante) {
      setComprobanteFile(null);
      if (comprobanteInputRef.current) {
        comprobanteInputRef.current.value = "";
      }
    }
  }, [metodoRequiereComprobante]);

  const modoCheckout = useMemo(() => {
    return String(data?.modo_pagos_checkout || "GLOBAL").toUpperCase();
  }, [data]);

  const reglaEnvio = useMemo(() => {
    return data?.regla_envio || null;
  }, [data]);

  const handleSeleccionar = (metodo) => {
    setMetodoSeleccionado(metodo);
  };

  const handleComprobanteChange = (e) => {
    const f = e.target.files?.[0];

    if (!f) {
      setComprobanteFile(null);
      return;
    }

    if (!f.type || !f.type.startsWith("image/")) {
      setError("Solo se permiten imágenes (JPG, PNG, WEBP, etc.).");
      e.target.value = "";
      setComprobanteFile(null);
      return;
    }

    setError("");
    setComprobanteFile(f);
  };

  const handleContinuar = async () => {
    if (!metodoSeleccionado || submitLockRef.current || submitting) return;

    if (metodoRequiereComprobante && !comprobanteFile) {
      setError("Debes adjuntar una imagen del comprobante de pago.");
      return;
    }

    submitLockRef.current = true;

    try {
      setSubmitting(true);
      setError("");

      const payload = new FormData();
      payload.append("grupo_reserva", grupo_reserva);
      payload.append(
        "tienda_metodo_pago_id",
        String(metodoSeleccionado.tienda_metodo_pago_id)
      );

      if (
        metodoSeleccionado.tienda_metodo_pago_regla_id != null &&
        metodoSeleccionado.tienda_metodo_pago_regla_id !== ""
      ) {
        payload.append(
          "tienda_metodo_pago_regla_id",
          String(metodoSeleccionado.tienda_metodo_pago_regla_id)
        );
      }

      if (comprobanteFile) {
        payload.append("comprobante_pago", comprobanteFile);
      }

      const response = await confirmarCheckoutPago(payload);

      try {
        sessionStorage.setItem(
          "checkout_pago",
          JSON.stringify({
            grupo_reserva,
            metodo_pago: metodoSeleccionado,
            resumen_pago: resumenPago,
            confirmacion: response,
          })
        );
      } catch {
        //
      }

      if (response?.data?.pedido_id) {
        sessionStorage.setItem("pedido_id", String(response.data.pedido_id));
      }

      if (response?.data?.no_po) {
        sessionStorage.setItem("pedido_no_po", String(response.data.no_po));
      }

      if (response?.flujo === "PASARELA") {
        navigate(`/store/checkout/payment/${grupo_reserva}`, {
          replace: true,
          state: {
            checkoutConfirmacion: response,
            resumenPago,
            metodoPago: metodoSeleccionado,
          },
        });
        return;
      }

      if (response?.flujo === "MANUAL") {
        navigate(`/store/checkout/pending/${grupo_reserva}`, {
          replace: true,
          state: {
            checkoutConfirmacion: response,
            resumenPago,
            metodoPago: metodoSeleccionado,
          },
        });
        return;
      }

      navigate(`/store/checkout/success/${grupo_reserva}`, {
        replace: true,
        state: {
          checkoutConfirmacion: response,
          resumenPago,
          metodoPago: metodoSeleccionado,
        },
      });
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          "No se pudo procesar el método de pago."
      );
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  useEffect(() => {
    return () => {
      submitLockRef.current = false;
    };
  }, []);

  const getMetodoNombre = (metodo) =>
    String(metodo?.nombre_visible || "Método de pago");

  const getMetodoDescripcion = (metodo) =>
    String(metodo?.descripcion || "").trim();

  const getMetodoInstrucciones = (metodo) =>
    String(metodo?.instrucciones || "").trim();

  const getTipoPagoNombre = (metodo) =>
    String(metodo?.tipo_pago?.nombre || "-");

  const getCuentas = (metodo) =>
    Array.isArray(metodo?.cuentas) ? metodo.cuentas : [];

  const subtotalProductos = Number(resumenPago?.subtotal_productos || 0);
  const coreChargeTotal = Number(resumenPago?.core_charge_total || 0);
  const totalEnvio = Number(resumenPago?.total_envio || 0);
  const totalPagar = Number(resumenPago?.total_pagar || 0);
  const cantidadProductos = Number(resumenPago?.cantidad_productos || 0);
  const metodoEnvioNombre = String(
    resumenPago?.metodo_envio?.nombre || "No definido"
  );
  const zonaEnvioNombre = String(
    resumenPago?.metodo_envio?.zona_nombre || "-"
  );

  if (loading) {
    return (
      <div className="checkout-pago-page">
        <div className="checkout-pago-shell">
          <CheckoutPagoStepper />
          <div style={styles.card}>
            <div style={styles.loader} />
            <p style={styles.loadingText}>Cargando métodos de pago...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !metodos.length) {
    return (
      <div className="checkout-pago-page">
        <div className="checkout-pago-shell">
          <CheckoutPagoStepper />
          <div style={styles.card}>
            <h2 style={styles.title}>Métodos de pago</h2>
            <p style={styles.error}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-pago-page">
      {submitting && (
        <div className="checkout-envio-loading-overlay">
          <div className="checkout-envio-loading-card">
            <div className="checkout-envio-loading-track">
              <div className="checkout-envio-loading-cart">
                <span className="checkout-envio-cart-handle" />
                <span className="checkout-envio-cart-basket" />
                <span className="checkout-envio-cart-wheel checkout-envio-cart-wheel--left" />
                <span className="checkout-envio-cart-wheel checkout-envio-cart-wheel--right" />
              </div>
            </div>

            <div className="checkout-envio-loading-spinner">
              <span />
              <span />
              <span />
            </div>

            <h3>Estamos procesando tu pago</h3>
            <p>Confirmando tu método de pago y preparando el siguiente paso…</p>
          </div>
        </div>
      )}

      <div className="checkout-pago-shell">
        <CheckoutPagoStepper />

        <div className="checkout-pago-layout">
          <div className="checkout-pago-main">
            <div style={styles.card}>
              <div style={styles.headerBlock}>
                <span className="checkout-pago-badge">Paso 3</span>
                <h1 style={styles.title}>Selecciona tu método de pago</h1>
                <p style={styles.subtitle}>
                  {modoCheckout === "POR_REGLA_ENVIO"
                    ? "Te mostramos los métodos de pago disponibles según la regla del método de envío seleccionado."
                    : "Te mostramos los métodos de pago activos configurados por la tienda."}
                </p>
              </div>

              {modoCheckout === "POR_REGLA_ENVIO" && reglaEnvio && (
                <div style={styles.ruleBox}>
                  <div style={styles.ruleBoxRow}>
                    <span style={styles.ruleLabel}>Zona aplicada</span>
                    <strong style={styles.ruleValue}>
                      {reglaEnvio.zona_nombre || "Sin zona"}
                    </strong>
                  </div>

                  {!!reglaEnvio.zona_descripcion && (
                    <p style={styles.ruleDescription}>
                      {reglaEnvio.zona_descripcion}
                    </p>
                  )}
                </div>
              )}

              {!metodos.length ? (
                <div style={styles.emptyBox}>
                  No hay métodos de pago disponibles para este checkout.
                </div>
              ) : (
                <div style={styles.list}>
                  {metodos.map((metodo) => {
                    const activo =
                      metodoSeleccionado?.tienda_metodo_pago_id ===
                      metodo.tienda_metodo_pago_id;

                    const cuentas = getCuentas(metodo);

                    return (
                      <button
                        key={`${metodo.tienda_metodo_pago_id}-${
                          metodo.tienda_metodo_pago_regla_id || "global"
                        }`}
                        type="button"
                        onClick={() => handleSeleccionar(metodo)}
                        style={{
                          ...styles.item,
                          ...(activo ? styles.itemActive : {}),
                        }}
                      >
                        <div style={styles.itemTop}>
                          <div>
                            <h3 style={styles.itemTitle}>
                              {getMetodoNombre(metodo)}
                            </h3>

                            {getMetodoDescripcion(metodo) ? (
                              <p style={styles.itemDescription}>
                                {getMetodoDescripcion(metodo)}
                              </p>
                            ) : null}
                          </div>

                          <div style={styles.radioWrap}>
                            <div
                              style={{
                                ...styles.radio,
                                ...(activo ? styles.radioActive : {}),
                              }}
                            >
                              {activo ? <div style={styles.radioInner} /> : null}
                            </div>
                          </div>
                        </div>

                        <div style={styles.metaRow}>
                          {Number(metodo?.requiere_comprobante || 0) === 1 && (
                            <span style={styles.metaPillAccent}>
                              Requiere comprobante
                            </span>
                          )}
                        </div>

                        {getMetodoInstrucciones(metodo) ? (
                          <div style={styles.infoBox}>
                            <strong>Instrucciones:</strong>
                            <p style={styles.infoText}>
                              {getMetodoInstrucciones(metodo)}
                            </p>
                          </div>
                        ) : null}

                        {cuentas.length > 0 && (
                          <div style={styles.bankBox}>
                            <p style={styles.bankTitle}>Cuentas disponibles</p>

                            <div style={styles.accountsList}>
                              {cuentas.map((cuenta) => (
                                <div
                                  key={cuenta.tienda_cuenta_pago_id}
                                  style={styles.accountCard}
                                >
                                  <div style={styles.bankGrid}>
                                    <div>
                                      <span style={styles.bankLabel}>Banco</span>
                                      <p style={styles.bankValue}>
                                        {cuenta.banco || "-"}
                                      </p>
                                    </div>

                                    <div>
                                      <span style={styles.bankLabel}>Titular</span>
                                      <p style={styles.bankValue}>
                                        {cuenta.titular || "-"}
                                      </p>
                                    </div>

                                    <div>
                                      <span style={styles.bankLabel}>
                                        Tipo de cuenta
                                      </span>
                                      <p style={styles.bankValue}>
                                        {cuenta.tipo_cuenta || "-"}
                                      </p>
                                    </div>

                                    <div>
                                      <span style={styles.bankLabel}>
                                        Número de cuenta
                                      </span>
                                      <p style={styles.bankValue}>
                                        {cuenta.numero_cuenta || "-"}
                                      </p>
                                    </div>
                                  </div>

                                  {cuenta.instrucciones ? (
                                    <div style={styles.bankInstructions}>
                                      <span style={styles.bankLabel}>
                                        Indicaciones
                                      </span>
                                      <p style={styles.bankValue}>
                                        {cuenta.instrucciones}
                                      </p>
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {error ? <p style={styles.error}>{error}</p> : null}
            </div>
          </div>

          <div className="checkout-pago-sidebar">
            <div className="checkout-pago-card--sticky" style={styles.summaryCard}>
              <h3 style={styles.summaryTitle}>Resumen de pago</h3>

              <div style={styles.summaryRow}>
                <span>Productos</span>
                <strong>{cantidadProductos}</strong>
              </div>

              <div style={styles.summaryRow}>
                <span>Subtotal productos</span>
                <strong>{formatMoney(subtotalProductos)}</strong>
              </div>

              {coreChargeTotal > 0 && (
                <div style={styles.summaryRow}>
                  <span>Core charge</span>
                  <strong>{formatMoney(coreChargeTotal)}</strong>
                </div>
              )}

              <div style={styles.summaryRow}>
                <span>Método de envío</span>
                <strong>{metodoEnvioNombre}</strong>
              </div>

              <div style={styles.summaryRow}>
                <span>Zona</span>
                <strong>{zonaEnvioNombre}</strong>
              </div>

              <div style={styles.summaryRow}>
                <span>Envío</span>
                <strong>{formatMoney(totalEnvio)}</strong>
              </div>

              <div style={styles.divider} />

              <div style={styles.summaryRow}>
                <span>Método seleccionado</span>
                <strong>
                  {metodoSeleccionado?.nombre_visible || "No seleccionado"}
                </strong>
              </div>

              <div style={styles.summaryRow}>
                <span>Tipo</span>
                <strong>{getTipoPagoNombre(metodoSeleccionado)}</strong>
              </div>

              <div style={styles.summaryRowGrand}>
                <span>Total a pagar</span>
                <strong>{formatMoney(totalPagar)}</strong>
              </div>

              {metodoRequiereComprobante && (
                <div className="checkout-pago-upload">
                  <div className="checkout-pago-upload__head">

                    <div className="checkout-pago-upload__head-copy">
                      <p className="checkout-pago-upload__eyebrow">
                        Validación requerida
                      </p>
                      <p className="checkout-pago-upload__title">
                        Sube tu comprobante de pago
                      </p>
                      <p className="checkout-pago-upload__hint">
                        Adjunta una imagen clara de tu transferencia, depósito o
                        comprobante manual.
                      </p>
                    </div>
                  </div>

                  <label className="checkout-pago-upload__dropzone">
                    <input
                      ref={comprobanteInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleComprobanteChange}
                      className="checkout-pago-upload__input"
                    />

                    {!comprobanteFile ? (
                      <div className="checkout-pago-upload__state checkout-pago-upload__state--empty">

                        <div className="checkout-pago-upload__content">
                          <strong className="checkout-pago-upload__main-text">
                            Selecciona una imagen
                          </strong>
                          <span className="checkout-pago-upload__sub-text">
                            Haz clic aquí para cargar tu comprobante
                          </span>
                        </div>

                        <div className="checkout-pago-upload__footer">
                          <span className="checkout-pago-upload__chip">JPG</span>
                          <span className="checkout-pago-upload__chip">PNG</span>
                          <span className="checkout-pago-upload__chip">WEBP</span>
                        </div>
                      </div>
                    ) : (
                      <div className="checkout-pago-upload__state checkout-pago-upload__state--filled">
                        <div className="checkout-pago-upload__success-icon">
                          ✓
                        </div>

                        <div className="checkout-pago-upload__content">
                          <span className="checkout-pago-upload__loaded-label">
                            Archivo cargado correctamente
                          </span>
                          <strong className="checkout-pago-upload__file-name">
                            {comprobanteFile.name}
                          </strong>
                          <span className="checkout-pago-upload__sub-text">
                            Haz clic nuevamente si deseas cambiar la imagen
                          </span>
                        </div>
                      </div>
                    )}
                  </label>
                </div>
              )}

              <button
                type="button"
                onClick={handleContinuar}
                disabled={
                  !metodoSeleccionado ||
                  submitting ||
                  (metodoRequiereComprobante && !comprobanteFile)
                }
                style={{
                  ...styles.continueBtn,
                  ...(!metodoSeleccionado ||
                  submitting ||
                  (metodoRequiereComprobante && !comprobanteFile)
                    ? styles.continueBtnDisabled
                    : {}),
                }}
              >
                {submitting ? "Procesando..." : "Continuar"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: {
    background: "#fff",
    borderRadius: "24px",
    padding: "28px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.1)",
  },
  summaryCard: {
    background: "#fff",
    borderRadius: "24px",
    padding: "24px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.1)",
  },
  headerBlock: {
    marginBottom: "22px",
  },
  title: {
    margin: "8px 0 10px",
    fontSize: "1.9rem",
    lineHeight: 1.1,
    color: "#111827",
  },
  subtitle: {
    margin: 0,
    color: "#6b7280",
    lineHeight: 1.6,
  },
  list: {
    display: "grid",
    gap: "16px",
  },
  item: {
    width: "100%",
    textAlign: "left",
    border: "1px solid #e5e7eb",
    borderRadius: "20px",
    padding: "18px",
    background: "#ffffff",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  itemActive: {
    border: "1px solid rgba(0, 0, 0, 0.32)",
    boxShadow: "0 0 0 3px rgba(0, 0, 0, 0.06)",
    background: "#f4f4f5",
  },
  itemTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "14px",
  },
  itemTitle: {
    margin: 0,
    fontSize: "1.08rem",
    color: "#111827",
  },
  itemDescription: {
    margin: "6px 0 0",
    color: "#6b7280",
    lineHeight: 1.5,
  },
  radioWrap: {
    flexShrink: 0,
  },
  radio: {
    width: "22px",
    height: "22px",
    borderRadius: "999px",
    border: "2px solid #d4d4d8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
  },
  radioActive: {
    borderColor: "#171717",
  },
  radioInner: {
    width: "10px",
    height: "10px",
    borderRadius: "999px",
    background: "#171717",
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "14px",
  },
  metaPillAccent: {
    display: "inline-flex",
    padding: "7px 12px",
    borderRadius: "999px",
    background: "#e5e5e5",
    color: "#262626",
    fontWeight: 700,
    fontSize: "0.85rem",
  },
  infoBox: {
    marginTop: "14px",
    padding: "14px",
    borderRadius: "14px",
    background: "#f9fafb",
    color: "#374151",
  },
  infoText: {
    margin: "6px 0 0",
    lineHeight: 1.6,
  },
  bankBox: {
    marginTop: "14px",
    padding: "16px",
    borderRadius: "16px",
    background: "#f4f4f5",
    border: "1px solid rgba(0, 0, 0, 0.1)",
  },
  bankTitle: {
    margin: "0 0 14px",
    fontWeight: 700,
    color: "#111827",
  },
  accountsList: {
    display: "grid",
    gap: "12px",
  },
  accountCard: {
    borderRadius: "14px",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    background: "#ffffff",
    padding: "14px",
  },
  bankGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
  },
  bankLabel: {
    display: "block",
    fontSize: "0.85rem",
    color: "#6b7280",
    marginBottom: "4px",
  },
  bankValue: {
    margin: 0,
    color: "#111827",
    fontWeight: 600,
    wordBreak: "break-word",
  },
  bankInstructions: {
    marginTop: "14px",
  },
  summaryTitle: {
    margin: "0 0 18px",
    fontSize: "1.15rem",
    color: "#111827",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "14px",
    color: "#374151",
  },
  summaryRowGrand: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    marginTop: "8px",
    marginBottom: "14px",
    color: "#111827",
    fontSize: "1.05rem",
    fontWeight: 700,
  },
  divider: {
    height: "1px",
    background: "#e5e7eb",
    margin: "16px 0",
  },
  continueBtn: {
    width: "100%",
    marginTop: "18px",
    border: "none",
    borderRadius: "16px",
    padding: "14px 18px",
    background: "#171717",
    color: "#fff",
    fontWeight: 700,
    fontSize: "1rem",
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(0, 0, 0, 0.12)",
  },
  continueBtnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  emptyBox: {
    padding: "18px",
    borderRadius: "16px",
    background: "#f9fafb",
    color: "#6b7280",
    border: "1px dashed #d1d5db",
  },
  error: {
    color: "#b91c1c",
    marginTop: "12px",
  },
  loader: {
    width: "48px",
    height: "48px",
    borderRadius: "999px",
    border: "4px solid #e5e7eb",
    borderTopColor: "#6366f1",
    animation: "spin 1s linear infinite",
    margin: "0 auto 14px",
  },
  loadingText: {
    textAlign: "center",
    color: "#6b7280",
    margin: 0,
  },
  ruleBox: {
    marginBottom: "18px",
    padding: "16px",
    borderRadius: "16px",
    background: "#f4f4f5",
    border: "1px solid rgba(0, 0, 0, 0.1)",
  },
  ruleBoxRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "10px",
  },
  ruleLabel: {
    color: "#6b7280",
  },
  ruleValue: {
    color: "#111827",
  },
  ruleDescription: {
    margin: "8px 0 0",
    color: "#4b5563",
    lineHeight: 1.5,
  },
};